import { type FieldValues, type UseControllerProps, useFormContext } from 'react-hook-form';

import { CreatableSelect as WFCreatableSelect, type CreatableSelectProps } from '@wf/components';

import { type LicenseFieldProps } from './LicenseFieldController';

import { useLicenseController } from '../../hooks/useLicenseController';

import { getUniqueArray } from '../../helpers/getUniqueArray';

export interface CreatableSelectAdditionalProps extends CreatableSelectProps<SelectOption>, LicenseFieldProps {
  regExp?: RegExp;
  maxCharacters?: number;
}

export const LicenseCreatableSelectField = ({
  name,
  defaultValue,
  configName,
  featureFlag,
  viewMode,
  maxCharacters,
  regExp,
  rules,
  shouldUnregister,
  control,
  disabled,
  ...rest
}: CreatableSelectAdditionalProps & UseControllerProps<FieldValues, any>) => {
  const { control: contextControl } = useFormContext();

  const {
    field: { value, onChange, onBlur, ...fieldRest },
    computedProps,
    config,
  } = useLicenseController({
    name,
    configName,
    featureFlag,
    control: control ?? contextControl,
    defaultValue: defaultValue ?? null,
    rules,
    shouldUnregister,
    viewMode,
    disabled,
  });

  const handleChange: typeof rest.onChange = (values, actionMeta) => {
    const unique = getUniqueArray(values as [], 'value');
    onChange(unique, actionMeta);

    if ('onChange' in rest && typeof rest.onChange === 'function') {
      rest.onChange(unique, actionMeta);
    }
  };

  const handleBlur: typeof rest.onBlur = (...args) => {
    onBlur();

    if ('onBlur' in rest && typeof rest.onBlur === 'function') {
      rest.onBlur(...args);
    }
  };

  const handleKeyDown: typeof rest.onKeyDown = (event) => {
    const { key, target } = event;

    if (
      maxCharacters &&
      (value?.map((i: SelectOption) => i.value).join('').length >= maxCharacters ||
        (target as HTMLInputElement).value.length >= maxCharacters) &&
      key !== 'Backspace' &&
      key !== 'Enter'
    ) {
      event.preventDefault();
    }

    if (regExp?.test(key)) {
      event.preventDefault();
    }
  };

  if (config.isHidden) {
    return <></>;
  }

  return (
    <WFCreatableSelect
      {...fieldRest}
      {...computedProps}
      {...rest}
      onKeyDown={handleKeyDown}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};
